import * as React from "react";
import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Link,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography
} from "@mui/material";
import { CheckCircle as CheckCircleIcon, Close as CloseIcon } from "@mui/icons-material";
import { formatRupees } from "./billzappPlans.jsx";
import AccountForms from "./accountForms.jsx";
import { useAccount } from "./accountContext.jsx";
import { requestPos } from "./posApi";
import { loadRazorpayCheckout } from "./razorpayCheckout";
import { POS_PORTAL_URL } from "../data/billzappPlans";

const EMPTY_FORM = { name: "", phone: "", email: "", businessName: "", address: "", city: "", state: "", pincode: "" };

const FIELDS = [
  { key: "name", label: "Full name", autoComplete: "name", sm: 6 },
  { key: "phone", label: "Mobile number", autoComplete: "tel", type: "tel", sm: 6 },
  { key: "email", label: "Email", autoComplete: "email", type: "email", sm: 6 },
  { key: "businessName", label: "Business name (optional)", autoComplete: "organization", sm: 6 },
  { key: "address", label: "Delivery address", autoComplete: "street-address", sm: 12, multiline: true },
  { key: "city", label: "City", autoComplete: "address-level2", sm: 4 },
  { key: "state", label: "State", autoComplete: "address-level1", sm: 4 },
  { key: "pincode", label: "PIN code", autoComplete: "postal-code", inputMode: "numeric", sm: 4 },
];

// The server's rules (parseBundleCustomer in POS-System-Backend/routes/billing.js), checked
// here too so mistakes show beside the field before anything is sent.
function validate(form) {
  const phone = form.phone.replace(/[\s-]/g, "").replace(/^(\+91|91|0)(?=\d{10}$)/, "");
  const errors = {};
  if (!form.name.trim()) errors.name = "Enter your name.";
  if (!/^[6-9]\d{9}$/.test(phone)) errors.phone = "Enter a 10-digit mobile number.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errors.email = "Enter a valid email address.";
  if (!form.address.trim()) errors.address = "Enter the delivery address.";
  if (!form.city.trim()) errors.city = "Enter the city.";
  if (!form.state.trim()) errors.state = "Enter the state.";
  if (!/^[1-9]\d{5}$/.test(form.pincode.trim())) errors.pincode = "Enter a 6-digit PIN code.";
  return errors;
}

const formatDate = (date) => new Date(date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

// Takes a "Pocket Billing Machine With Software" order: the buyer logs in or creates their
// BillZapp account, enters delivery details, then pays through Razorpay Checkout. The BillZapp
// server sets the price, confirms the payment and adds the year of software to the account.
export default function BillingMachineOrderDialog({ plan, printer, title, onClose }) {
  const { status, profile, request, signOut } = useAccount();
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState("form"); // "form" | "paying" | "done"
  const [message, setMessage] = useState("");
  // Once paid: { orderNumber, softwareAdded } (year on the account) or { orderNumber, claimUrl }.
  const [result, setResult] = useState(null);

  const planPaise = plan.annual.amountPaise;
  const totalPaise = printer.amountPaise + planPaise;
  const needsAccount = status !== "signedIn" && step !== "done";

  // Starts the delivery form from the account's business details.
  useEffect(() => {
    if (!profile) return;
    setForm((current) => ({
      ...current,
      email: current.email || profile.email,
      phone: current.phone || profile.phone,
      businessName: current.businessName || profile.businessName,
      address: current.address || profile.address,
    }));
  }, [profile]);

  const updateField = (key) => (event) => {
    setForm({ ...form, [key]: event.target.value });
    if (errors[key]) setErrors({ ...errors, [key]: undefined });
  };

  const handlePay = async (event) => {
    event.preventDefault();
    const fieldErrors = validate(form);
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    setMessage("");
    setStep("paying");
    try {
      // Sent with the website login, so the server puts the year on this account.
      const [order] = await Promise.all([
        request("/billing/bundle/createOrder", { method: "POST", body: { planId: plan.id, printerId: printer.id, customer: form } }),
        loadRazorpayCheckout(),
      ]);

      const checkout = new window.Razorpay({
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        order_id: order.orderId,
        name: "Sateri Electronic Services",
        description: title,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        notes: { orderNumber: order.orderNumber },
        theme: { color: "#1976d2" },
        handler: async (response) => {
          try {
            setResult(await requestPos("/billing/bundle/verifyPayment", { method: "POST", body: response }));
          } catch (err) {
            // The money has gone through; Razorpay's webhook confirms the order, adds the year
            // and sends the confirmation email.
            setResult({ orderNumber: order.orderNumber });
          }
          setStep("done");
        },
        modal: {
          ondismiss: () => {
            setStep("form");
            setMessage((current) => current || "Payment was cancelled. You can try again.");
          },
        },
      });
      checkout.on("payment.failed", (response) => {
        setMessage(response.error?.description || "The payment failed. Please try again.");
      });
      checkout.open();
    } catch (err) {
      setStep("form");
      setMessage(err.message);
    }
  };

  return (
    <Dialog
      open
      onClose={step === "paying" ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      // Razorpay's window opens on top of this dialog and needs the keyboard.
      disableEnforceFocus={step === "paying"}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
        {step === "done" ? "Order placed" : title}
        <IconButton onClick={onClose} size="small" aria-label="close" disabled={step === "paying"}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {step === "done" ? (
        <DialogContent>
          <Stack alignItems="center" textAlign="center" spacing={1.5} sx={{ py: 1 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 56 }} />
            <Typography variant="h6" fontWeight="bold">
              Thank you, {form.name.trim()}!
            </Typography>
            <Typography color="text.secondary">
              Your order number is <b>{result.orderNumber}</b>. We have emailed the details to{" "}
              <b>{form.email.trim()}</b> and will call you on <b>{form.phone.trim()}</b> to arrange
              delivery of your printer.
            </Typography>
          </Stack>
          <Divider sx={{ my: 3 }} />
          {result.softwareAdded ? (
            <>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Your year of BillZapp {result.softwareAdded.planName} is ready
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                It has been added to <b>{result.softwareAdded.businessName}</b>, and BillZapp now runs
                until <b>{formatDate(result.softwareAdded.periodEnd)}</b>. Log in to BillZapp with the
                same username and password.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <Button href={POS_PORTAL_URL} variant="contained" size="large" fullWidth>
                  Open BillZapp
                </Button>
                <Button href="/account?tab=purchases" variant="outlined" size="large" fullWidth>
                  View my purchases
                </Button>
              </Stack>
            </>
          ) : (
          <>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Add your year of BillZapp {plan.name}
          </Typography>
          {result.claimUrl ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create your BillZapp account, or log in if you already have one, and the year is
                added to it. The same link is in your confirmation email.
              </Typography>
              <Button href={result.claimUrl} variant="contained" size="large" fullWidth>
                Set up BillZapp
              </Button>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              We are still confirming your payment. Once it is confirmed the year is added to your
              account; you can check it under <Link href="/account?tab=purchases">My Account</Link>.
            </Typography>
          )}
          </>
          )}
        </DialogContent>
      ) : (
        <DialogContent>
          <Stepper activeStep={needsAccount ? 0 : 1} alternativeLabel sx={{ mb: 3 }}>
            <Step><StepLabel>Your BillZapp account</StepLabel></Step>
            <Step><StepLabel>Delivery and payment</StepLabel></Step>
          </Stepper>

          {status === "checking" ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : needsAccount ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Your year of BillZapp {plan.name} goes onto your BillZapp account. Log in, or create
                one in a minute.
              </Typography>
              {message && <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>}
              <AccountForms planId={plan.id} onDone={() => setMessage("")} />
            </>
          ) : (
          <Box component="form" noValidate onSubmit={handlePay}>
            <Alert severity="info" icon={false} sx={{ mb: 2 }}>
              Ordering for <b>{profile.businessName}</b> (username {profile.username}).{" "}
              <Link component="button" type="button" onClick={signOut} disabled={step === "paying"} sx={{ verticalAlign: "baseline" }}>
                Not you? Log out
              </Link>
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter where the printer should be delivered. Your order confirmation is sent to your email.
            </Typography>
            {message && <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>}

            <Grid container spacing={2}>
              {FIELDS.map((field) => (
                <Grid item xs={12} sm={field.sm} key={field.key}>
                  <TextField
                    label={field.label}
                    value={form[field.key]}
                    onChange={updateField(field.key)}
                    error={Boolean(errors[field.key])}
                    helperText={errors[field.key]}
                    type={field.type || "text"}
                    autoComplete={field.autoComplete}
                    inputProps={field.inputMode ? { inputMode: field.inputMode } : undefined}
                    multiline={field.multiline}
                    minRows={field.multiline ? 2 : undefined}
                    disabled={step === "paying"}
                    fullWidth
                  />
                </Grid>
              ))}
            </Grid>

            <Stack spacing={0.75} sx={{ mt: 3, mb: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography color="text.secondary">BillZapp {printer.name} receipt printer</Typography>
                <Typography>{formatRupees(printer.amountPaise)}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography color="text.secondary">BillZapp {plan.name} (1 year)</Typography>
                <Typography>{formatRupees(planPaise)}</Typography>
              </Box>
              <Divider />
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography fontWeight="bold">Total</Typography>
                <Typography fontWeight="bold">{formatRupees(totalPaise)}</Typography>
              </Box>
            </Stack>

            <Button type="submit" variant="contained" size="large" fullWidth disabled={step === "paying"}>
              {step === "paying" ? <CircularProgress size={26} color="inherit" /> : `Pay ${formatRupees(totalPaise)}`}
            </Button>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1.5 }}>
              Payments are processed securely by Razorpay. Prefer to order by phone? Call +91 98224 12353.
            </Typography>
          </Box>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
}
